// frontend/src/pages/doctor/ManageConsultations.js
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {  Search, Calendar } from 'lucide-react';
import doctorService from '../../services/doctorService';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Modal from '../../components/common/Modal';

const ManageConsultations = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const [filters, setFilters] = useState({
    status: searchParams.get('status') || '',
    type: searchParams.get('type') || '',
    searchTerm: ''
  });

  useEffect(() => {
    fetchConsultations();
  }, [filters.status, filters.type]);

  const fetchConsultations = async () => {
    try {
      setLoading(true);
      const response = await doctorService.getConsultations({
        status: filters.status,
        type: filters.type,
        limit: 100
      });

      if (response.success) {
        setConsultations(response.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch consultations');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const openModal = (consultation, type) => {
    setSelectedConsultation(consultation);
    setActionType(type);
    setShowModal(true);
    
    if (type === 'accept' && consultation.preferredTime) {
      setScheduledTime(new Date(consultation.preferredTime).toISOString().slice(0, 16));
    } else {
      setScheduledTime('');
    }
    setRejectReason('');
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedConsultation(null);
    setActionType('');
    setScheduledTime('');
    setRejectReason('');
  };

  const handleAcceptConsultation = async () => {
    if (!scheduledTime) {
      alert('Please select a scheduled time');
      return;
    }

    try {
      setActionLoading(true);
      const response = await doctorService.acceptConsultation(
        selectedConsultation._id,
        scheduledTime
      );

      if (response.success) {
        await fetchConsultations();
        closeModal();
        alert('Consultation accepted successfully!');
      }
    } catch (err) {
      alert(err.message || 'Failed to accept consultation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectConsultation = async () => {
    if (!rejectReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    try {
      setActionLoading(true);
      const response = await doctorService.rejectConsultation(
        selectedConsultation._id,
        rejectReason
      );

      if (response.success) {
        await fetchConsultations();
        closeModal();
        alert('Consultation rejected');
      }
    } catch (err) {
      alert(err.message || 'Failed to reject consultation');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredConsultations = consultations.filter(consultation =>
    !filters.searchTerm ||
    consultation.patient?.name?.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
    consultation.symptoms?.toLowerCase().includes(filters.searchTerm.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pending' },
      accepted: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Accepted' },
      completed: { bg: 'bg-green-100', text: 'text-green-700', label: 'Completed' },
      rejected: { bg: 'bg-red-100', text: 'text-red-700', label: 'Rejected' },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Cancelled' }
    };

    const badge = badges[status] || badges.pending;
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Manage Consultations</h1>
        <p className="text-gray-600 mt-1">Review and manage consultation requests</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by patient name or symptoms..."
                value={filters.searchTerm}
                onChange={(e) => setFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="accepted">Accepted</option>
              <option value="completed">Completed</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Type Filter */}
          <div>
            <select
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All Types</option>
              <option value="online">Online</option>
              <option value="physical">Physical</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Consultations List */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {filteredConsultations.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Calendar className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p className="text-lg">No consultations found</p>
            <p className="text-sm mt-1">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Patient
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Symptoms
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date & Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredConsultations.map((consultation) => (
                  <tr key={consultation._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {consultation.patient?.name || 'Anonymous'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {consultation.patient?.phone || 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        consultation.type === 'online'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {consultation.type}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate">
                        {consultation.symptoms}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {consultation.scheduledAt
                          ? new Date(consultation.scheduledAt).toLocaleDateString()
                          : new Date(consultation.createdAt).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-gray-500">
                        {consultation.scheduledAt
                          ? new Date(consultation.scheduledAt).toLocaleTimeString()
                          : new Date(consultation.createdAt).toLocaleTimeString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(consultation.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      KES {consultation.fee.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      {consultation.status === 'pending' && (
                        <>
                          <button
                            onClick={() => openModal(consultation, 'accept')}
                            className="text-green-600 hover:text-green-900"
                          >
                            Accept
                          </button>
                          <button
                            onClick={() => openModal(consultation, 'reject')}
                            className="text-red-600 hover:text-red-900"
                          >
                            Reject
                          </button>
                        </>
                      )}
                      {consultation.status === 'accepted' && (
                        <Link
                          to={`/doctor/consultation/${consultation._id}`}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          Start
                        </Link>
                      )}
                      <Link
                        to={`/doctor/consultations/${consultation._id}`}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Action Modal */}
      {showModal && selectedConsultation && (
        <Modal
          isOpen={showModal}
          onClose={closeModal}
          title={actionType === 'accept' ? 'Accept Consultation' : 'Reject Consultation'}
        >
          <div className="space-y-4">
            {/* Patient Info */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Patient Information</h4>
              <p className="text-sm text-gray-600">
                <strong>Name:</strong> {selectedConsultation.patient?.name || 'Anonymous'}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Symptoms:</strong> {selectedConsultation.symptoms}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Type:</strong> {selectedConsultation.type}
              </p>
              {selectedConsultation.preferredTime && (
                <p className="text-sm text-gray-600">
                  <strong>Preferred Time:</strong>{' '}
                  {new Date(selectedConsultation.preferredTime).toLocaleString()}
                </p>
              )}
            </div>

            {actionType === 'accept' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Schedule Consultation Time *
                </label>
                <input
                  type="datetime-local"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Rejection *
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows="4"
                  placeholder="Please provide a reason..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-3">
              <button
                onClick={closeModal}
                disabled={actionLoading}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={actionType === 'accept' ? handleAcceptConsultation : handleRejectConsultation}
                disabled={actionLoading}
                className={`px-4 py-2 rounded-lg text-white disabled:opacity-50 ${
                  actionType === 'accept'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {actionLoading ? 'Processing...' : (actionType === 'accept' ? 'Accept' : 'Reject')}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ManageConsultations;